import { useEffect, useMemo, useState } from 'react';
import { fetchUserPrizeStatus, getPrizePoolStatusV2, getMultipleLotteryResultRequest } from 'lib/apis';
import { ACT_AWARD_TYPE, DELIVER_STATUS } from 'lib/constants/award';
import { showTipsDialog } from 'lib/components/TipsDialog';
import { useUserOrderStore } from 'lib/store/userOrderStore';

// 定义参数类型接口
interface UseAwardStatusParams {
  activityId?: number;
  actAwardType?: number;
}

// 定义奖品信息类型
interface PrizeInfo {
  prizeActId?: string | number;
  act_id?: string | number;
  up_deliver_status?: number;
  prizeType?: string | number;
  prize_type?: string | number;
  redeemCode?: string;
  redeemNo?: string;
  redeemToken?: string;
  prizeName?: string;
  prizePic?: string;
  prizeUrl?: string;
  prizeDetail?: string;
  prizeId?: string | number;
  deliverStatus?: number;
  [key: string]: any;
}

// 定义返回值类型接口
interface UseAwardStatusReturn {
  prizeInfo: PrizeInfo;
  isReceived: boolean;
  isPoolEmpty: boolean;
  fetchPrizeList: () => void;
}

export const useAwardStatus = ({ activityId, actAwardType }: UseAwardStatusParams): UseAwardStatusReturn => {
  const [prizeInfo, setPrizeInfo] = useState<PrizeInfo>({});
  const [isReceived, setIsReceived] = useState<boolean>(false);
  const [isPoolEmpty, setIsPoolEmpty] = useState<boolean>(false);
  const isLottery = useMemo(() => actAwardType === ACT_AWARD_TYPE.LOTTERY, [actAwardType]);

  const { userOrderInfo } = useUserOrderStore((state) => ({
    userOrderInfo: state.userOrderInfo,
  }));
  const { isSubscriber } = userOrderInfo;

  // 统一奖品数据格式
  const normalizePrizeData = (prize: any): PrizeInfo => {
    if (isLottery) {
      // 抽奖接口返回的驼峰格式
      return {
        ...prize,
      };
    }
    // 必得接口返回的下划线格式
    return {
      prizeType: prize.prize_type,
      redeemCode: prize.redeem_code || '',
      redeemNo: prize.redeem_no || '',
      redeemToken: prize.redeem_token || '',
      upDeliverStatus: prize.up_deliver_status,
      ...prize,
    };
  };

  const fetchPrizeList = async () => {
    if (!isSubscriber || !activityId) return;

    let res;
    let prize;

    // 根据活动类型调用不同接口
    if (!isLottery) {
      // 必得类型
      res = await fetchUserPrizeStatus({ actId: activityId });
      if (res?.code) {
        showTipsDialog({ content: '活动太火爆了，请稍后再试' });
        return;
      }
      prize = res?.data.user_prizes?.[0];
    } else {
      // 抽奖类型
      res = await getMultipleLotteryResultRequest({ actId: activityId });
      if (res?.code) {
        showTipsDialog({ content: '活动太火爆了，请稍后再试' });
        return;
      }
      prize = res?.data.drawResult?.[0];
    }
    if (!prize) {
      setIsReceived(false);
      setPrizeInfo({});
      return;
    }

    // 统一数据格式
    const normalizedPrize = normalizePrizeData(prize);

    // 检查是否已领取
    const deliverStatus = isLottery ? normalizedPrize.deliverStatus : normalizedPrize.upDeliverStatus;
    const isDelivered = deliverStatus === DELIVER_STATUS.DELIVERED;

    setIsReceived(isDelivered);
    setPrizeInfo(normalizedPrize);
  };

  // 查询库存（仅必得类型需要）
  const fetchPoolStatus = async () => {
    if (!isSubscriber || isLottery) return;

    try {
      const res = await getPrizePoolStatusV2({ actId: activityId });
      if (res?.code) {
        return;
      }
      const { list = [] } = res?.data;
      const isPoolEmpty = parseInt(list[0]?.remain, 10) === 0;
      setIsPoolEmpty(isPoolEmpty);
    } catch (error) {
      console.error('查询库存失败:', error);
    }
  };

  useEffect(() => {
    if (!activityId) {
      return;
    }

    // 获取用户奖品列表
    fetchPrizeList();

    // 只有必得类型才需要查询库存
    if (!isLottery) {
      fetchPoolStatus();
    } else {
      setIsPoolEmpty(false);
    }
  }, [activityId, isSubscriber, isLottery]);

  return {
    prizeInfo,
    isReceived,
    isPoolEmpty,
    fetchPrizeList,
  };
};
